import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import AdminShell from '../components/admin/AdminShell';
import Icon from '../components/Icon';
import { formatDateWithTime, formatMoney } from '../utils/format';
import { SkeletonAdminBody } from '../components/Skeleton';

function DetailPill({ label, value, tone = 'text-slate-900' }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div className={`mt-2 break-words text-sm font-medium ${tone}`}>{value || '-'}</div>
    </div>
  );
}

function extractMpesaMessage(rawValue) {
  const text = String(rawValue || '').replace(/\s+/g, ' ').trim();
  if (!text) return null;

  const match = text.match(
    /([A-Z0-9]{10})\s+Confirmed\.\s*Ksh\s*([\d,]+(?:\.\d{2})?)\s+sent to\s+(.+?)\s+for account\s+([A-Za-z0-9-]+)\s+on\s+(\d{1,2}\/\d{1,2}\/\d{2,4})\s+at\s+(\d{1,2}:\d{2}\s*[AP]M)/i
  );

  if (!match) return null;

  return {
    transactionId: String(match[1] || '').trim().toUpperCase(),
    amount: Number(String(match[2] || '0').replace(/,/g, '')),
    payee: String(match[3] || '').trim(),
    billRefNumber: String(match[4] || '').trim(),
    messageDate: String(match[5] || '').trim(),
    messageTime: String(match[6] || '').trim().toUpperCase()
  };
}

function resolveTransactionId(rawValue) {
  const parsed = extractMpesaMessage(rawValue);
  if (parsed?.transactionId) return { transactionId: parsed.transactionId, parsedMessage: parsed };

  const fallback = String(rawValue || '').trim().toUpperCase();
  return {
    transactionId: fallback,
    parsedMessage: null
  };
}

function resolveAccountReference(rawValue) {
  const raw = String(rawValue || '').trim();
  if (!raw) return { isValid: false, module: null, idNumber: '' };
  if (/^ut[\s-]*$/i.test(raw) || /^u[\s-]*$/i.test(raw)) {
    return { isValid: false, module: null, idNumber: '' };
  }

  const utilityMatch = raw.match(/^u(?:t)?[\s-]*([A-Za-z0-9].*)$/i);
  if (utilityMatch) {
    return {
      isValid: true,
      module: 'utilities',
      idNumber: String(utilityMatch[1] || '').trim()
    };
  }

  if (/^u/i.test(raw)) return { isValid: false, module: null, idNumber: '' };
  return { isValid: true, module: 'rentals', idNumber: raw };
}

function AdminMpesaReconciliationPage() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [accountInput, setAccountInput] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [result, setResult] = useState(null);

  const canUseReconciliation = Boolean(
    currentUser?.role === 'A' || currentUser?.canUseMpesaReconciliation
  );
  const canRecoverReconciliation = Boolean(
    currentUser?.role === 'A' || currentUser?.canRecoverMpesaReconciliation
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('.dropdown-container')) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const resetForm = useCallback(() => {
    setInputValue('');
    setAccountInput('');
    setAdminPassword('');
    setError('');
    setMessage('');
    setResult(null);
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    navigate('/login');
  }, [navigate]);

  useEffect(() => {
    let active = true;

    const loadUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await api.get('/auth/me');
        if (!active) return;
        const userData = response?.data || null;
        if (!userData?._id) {
          navigate('/login');
          return;
        }
        if (!(userData.role === 'A' || userData.canUseMpesaReconciliation)) {
          navigate('/dashboard');
          return;
        }
        setCurrentUser(userData);
      } catch (_) {
        if (!active) return;
        localStorage.removeItem('token');
        navigate('/login');
      } finally {
        if (active) setLoadingUser(false);
      }
    };

    loadUser();
    return () => {
      active = false;
    };
  }, [navigate]);

  const loadSearch = useCallback(async (rawValue = inputValue) => {
    const { transactionId: normalizedId, parsedMessage } = resolveTransactionId(rawValue);
    if (!normalizedId) {
      const nextError = 'Paste an M-Pesa message or enter a transaction ID first.';
      setError(nextError);
      window.alert(nextError);
      return;
    }

    try {
      setSearching(true);
      setError('');
      setMessage('');
      const response = await api.post('/frp/admin/mpesa/reconciliation/live-check', { transactionId: normalizedId });
      const data = response?.data || null;
      setResult({
        ...(data || {}),
        transactionId: normalizedId,
        parsedMessage
      });
    } catch (searchError) {
      console.error('Failed to search reconciliation data:', searchError);
      const nextError = searchError?.response?.data?.error || 'Failed to load reconciliation data.';
      setError(nextError);
      setResult(null);
      window.alert(nextError);
    } finally {
      setSearching(false);
    }
  }, [inputValue]);

  const handleRecover = useCallback(async () => {
    const { transactionId: normalizedId, parsedMessage } = resolveTransactionId(inputValue);
    const resolvedBillRefNumber = String(accountInput || parsedMessage?.billRefNumber || '').trim();
    const resolvedAccountReference = resolveAccountReference(resolvedBillRefNumber);
    if (!normalizedId) {
      const nextError = 'Enter the transaction number or paste the M-Pesa message first.';
      setError(nextError);
      window.alert(nextError);
      return;
    }

    if (!resolvedBillRefNumber) {
      const nextError = 'Enter the account / BillRefNumber so recovery knows which wallet to credit.';
      setError(nextError);
      window.alert(nextError);
      return;
    }

    if (!resolvedAccountReference.isValid) {
      const nextError = 'Use an ID number for Rentals, or U / UT followed by the ID number for Bills & Invoices.';
      setError(nextError);
      window.alert(nextError);
      return;
    }

    if (!adminPassword.trim()) {
      const nextError = 'Enter your current PIN/password to recover this transaction.';
      setError(nextError);
      window.alert(nextError);
      return;
    }

    try {
      setRecovering(true);
      setError('');
      setMessage('');
      const response = await api.post('/frp/admin/mpesa/reconciliation/recover', {
        transactionId: normalizedId,
        liveCheckRequestId: result?.liveCheck?.requestId || result?.mpesaCheck?.requestId || undefined,
        input: inputValue,
        message: inputValue,
        billRefNumber: resolvedBillRefNumber,
        amount: parsedMessage?.amount || undefined,
        adminPassword: adminPassword.trim()
      });

      const nextMessage = response?.data?.message || 'Transaction recovered successfully.';
      setMessage(nextMessage);
      setAdminPassword('');
      const refreshedMatches = response?.data?.localMatches || { walletMatches: [], paymentMatches: [], foundInApp: false };
      setResult((current) => ({
        ...(current || {}),
        localMatches: refreshedMatches,
        reconciliation: {
          existsInMpesa: true,
          existsInApp: !!refreshedMatches?.foundInApp,
          missingInApp: !refreshedMatches?.foundInApp
        }
      }));
      window.alert(nextMessage);
    } catch (recoverError) {
      console.error('Failed to recover transaction locally:', recoverError);
      const nextError = recoverError?.response?.data?.error || 'Failed to recover the transaction locally.';
      setError(nextError);
      window.alert(nextError);
    } finally {
      setRecovering(false);
    }
  }, [accountInput, adminPassword, inputValue, result?.liveCheck?.requestId, result?.mpesaCheck?.requestId]);

  useEffect(() => {
    const requestId = String(result?.liveCheck?.requestId || result?.mpesaCheck?.requestId || '').trim();
    const status = String(result?.liveCheck?.status || result?.mpesaCheck?.status || '').toLowerCase();
    if (!requestId || status !== 'pending') return undefined;

    const interval = setInterval(async () => {
      try {
        const response = await api.get(`/frp/admin/mpesa/reconciliation/live-check/${encodeURIComponent(requestId)}`);
        const data = response?.data || null;
        setResult((current) => ({
          ...(current || {}),
          ...(data || {}),
          parsedMessage: current?.parsedMessage || extractMpesaMessage(inputValue)
        }));
      } catch (pollError) {
        console.error('Failed to refresh live M-Pesa check:', pollError);
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [inputValue, result?.liveCheck?.requestId, result?.liveCheck?.status, result?.mpesaCheck?.requestId, result?.mpesaCheck?.status]);

  const parsedMessage = result?.parsedMessage || extractMpesaMessage(inputValue);
  const localMatches = result?.localMatches || { walletMatches: [], paymentMatches: [], foundInApp: false };
  const mpesaCheck = result?.mpesaCheck || result?.liveCheck || null;
  const walletMatches = useMemo(
    () => (Array.isArray(localMatches.walletMatches) ? localMatches.walletMatches : []),
    [localMatches.walletMatches]
  );
  const paymentMatches = useMemo(
    () => (Array.isArray(localMatches.paymentMatches) ? localMatches.paymentMatches : []),
    [localMatches.paymentMatches]
  );
  const hasWalletMatch = walletMatches.length > 0;
  const hasPaymentMatch = paymentMatches.length > 0;
  const hasAnyLocalMatch = hasWalletMatch || hasPaymentMatch;
  const resolvedTransactionId = String(result?.transactionId || parsedMessage?.transactionId || inputValue || '').trim().toUpperCase();
  const mpesaStatus = String(mpesaCheck?.status || '').toLowerCase();
  const existsInMpesa = mpesaCheck?.existsInMpesa === true;
  const mpesaStatusLabel = existsInMpesa ? 'Found' : mpesaStatus === 'pending' ? 'Checking...' : mpesaCheck ? 'Not found' : 'Not checked';
  const canRecoverMissing = Boolean(
    canRecoverReconciliation &&
    resolvedTransactionId &&
    existsInMpesa &&
    !hasAnyLocalMatch
  );
  const recoveryReference = resolveAccountReference(accountInput || parsedMessage?.billRefNumber || '');
  const recoveryDestination = recoveryReference.module === 'utilities' ? 'Bills & Invoices wallet' : 'Rentals wallet';

  useEffect(() => {
    if (parsedMessage?.billRefNumber) {
      setAccountInput(parsedMessage.billRefNumber);
    }
  }, [parsedMessage?.billRefNumber]);

  const appDetailLines = useMemo(() => {
    const lines = [];

    if (walletMatches.length > 0) {
      const firstWallet = walletMatches[0];
      lines.push(`Wallet matches: ${walletMatches.length}`);
      lines.push(`Wallet: ${firstWallet.walletLabel || '-'}`);
      lines.push(`Wallet amount: Ksh ${formatMoney(firstWallet.transaction?.amount || 0)}`);
      lines.push(`Wallet date: ${firstWallet.transaction?.timestamp ? formatDateWithTime(firstWallet.transaction.timestamp) : '-'}`);
    } else {
      lines.push('Wallet matches: 0');
    }

    if (paymentMatches.length > 0) {
      const firstPayment = paymentMatches[0];
      lines.push(`Payment matches: ${paymentMatches.length}`);
      lines.push(`Payment amount: Ksh ${formatMoney(firstPayment.amount || firstPayment.paymentAmount || 0)}`);
      lines.push(`Payment date: ${firstPayment.createdAt || firstPayment.timestamp ? formatDateWithTime(firstPayment.createdAt || firstPayment.timestamp) : '-'}`);
    } else {
      lines.push('Payment matches: 0');
    }

    return lines;
  }, [paymentMatches, walletMatches]);

  const pageBody = (
    <div className="space-y-5">
      <div className="mb-4 -mx-4 rounded-b-[2rem] border border-emerald-600 bg-emerald-600 px-4 py-6 text-white shadow-lg sm:-mx-6 sm:px-6 sm:py-7">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/90">
                M-Pesa reconciliation
              </div>
              <h1 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">Check transaction</h1>
              <p className="mt-2 max-w-2xl text-sm text-emerald-100">
                Paste the transaction code.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex flex-col gap-4">
          <div>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Transaction ID or M-Pesa message
            </label>
            <textarea
              value={inputValue}
              onChange={(event) => {
                const nextValue = event.target.value;
                setInputValue(nextValue);
                const nextParsed = extractMpesaMessage(nextValue);
                if (nextParsed?.billRefNumber) {
                  setAccountInput(nextParsed.billRefNumber);
                }
              }}
              placeholder="Paste UDDI40N6GO Confirmed... or just the M-Pesa code"
              rows={4}
              className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-emerald-500 focus:bg-white focus:ring-4 focus:ring-emerald-100"
            />
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              onClick={() => loadSearch(inputValue)}
              disabled={searching}
              className="inline-flex items-center justify-center rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {searching ? 'Checking...' : 'Check transaction'}
            </button>
            <button
              onClick={resetForm}
              className="inline-flex items-center justify-center rounded-2xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Clear
            </button>
          </div>
        </div>

        {message && <div className="mt-3 text-xs text-emerald-700">{message}</div>}
        {error && <div className="mt-3 text-xs text-rose-700">{error}</div>}
      </div>

      {result && (
        <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Transaction presence</h2>
              <p className="text-sm text-slate-500">One view for the live M-Pesa check result, app match details, and recovery.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className={`rounded-full px-3 py-1 text-xs font-semibold ${existsInMpesa ? 'bg-emerald-50 text-emerald-700' : mpesaStatus === 'pending' ? 'bg-amber-50 text-amber-700' : 'bg-rose-50 text-rose-700'}`}>
                On M-Pesa: {mpesaStatusLabel}
              </div>
              <button
                type="button"
                onClick={resetForm}
                className="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Clear form
              </button>
            </div>
          </div>

          <div className="grid gap-3">
            <DetailPill
              label="Status And App Detail"
              value={
                <div className="space-y-2">
                  <div>
                    <span className="text-slate-500">Transaction ID:</span> {result.transactionId || parsedMessage?.transactionId || '-'}
                  </div>
                  <div>
                    <span className="text-slate-500">Amount:</span> {parsedMessage?.amount ? `Ksh ${formatMoney(parsedMessage.amount)}` : '-'}
                  </div>
                  <div>
                    <span className="text-slate-500">On M-Pesa:</span>{' '}
                    <span className={existsInMpesa ? 'text-emerald-700' : mpesaStatus === 'pending' ? 'text-amber-700' : 'text-rose-700'}>
                      {mpesaStatusLabel}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">In App DB:</span>{' '}
                    <span className={hasAnyLocalMatch ? 'text-emerald-700' : 'text-amber-700'}>
                      {hasAnyLocalMatch ? 'Found' : 'Not found'}
                    </span>
                  </div>
                  {appDetailLines.map((line) => (
                    <div key={line}>{line}</div>
                  ))}
                </div>
              }
            />
          </div>

          {canRecoverMissing && (
            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex flex-col items-center gap-4 text-center">
                <div className="max-w-2xl">
                  <div className="text-sm font-semibold text-amber-900">Recover missing local transaction</div>
                  <div className="mt-1 text-sm text-amber-800">
                    M-Pesa has confirmed this transaction and the app has not recorded it yet. Use an ID number for Rentals, or U / UT followed by the ID number for Bills &amp; Invoices. Confirm with your current PIN so the saved record shows who reconciled it.
                  </div>
                  <div className="mt-2 text-sm font-semibold text-amber-900">
                    Destination: {recoveryReference.isValid ? recoveryDestination : 'Enter a valid account reference'}
                  </div>
                </div>
                <div className="grid w-full gap-4 lg:grid-cols-[minmax(0,20rem)_minmax(0,20rem)_auto] lg:items-end">
                  <div className="w-full text-left">
                    <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-900">
                      Account reference
                    </label>
                    <input
                      value={accountInput}
                      onChange={(event) => setAccountInput(event.target.value)}
                      placeholder="ID number, U12345678, or UT12345678"
                      className="w-full rounded-2xl border border-amber-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-amber-500 focus:ring-4 focus:ring-amber-100"
                    />
                  </div>
                  <div className="w-full text-left">
                    <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-900">
                      Current PIN
                    </label>
                    <p className="mb-1.5 text-[11px] text-amber-900/80">
                      This is the PIN of the person doing the reconciliation/logged in, not the customer's PIN.
                    </p>
                    <input
                      type="password"
                      value={adminPassword}
                      onChange={(event) => setAdminPassword(event.target.value)}
                      placeholder="Enter your current PIN"
                      className="w-full rounded-2xl border border-amber-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-amber-500 focus:ring-4 focus:ring-amber-100"
                    />
                  </div>
                  <button
                    onClick={handleRecover}
                    disabled={recovering}
                    className="inline-flex items-center justify-center rounded-2xl bg-amber-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {recovering ? 'Recovering...' : 'Recover locally'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {!canRecoverReconciliation && existsInMpesa && !hasAnyLocalMatch && (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
              Search is enabled for your account, but reconcile access is still off. Ask an admin to enable `Reconcile` for your agent profile.
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (loadingUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
        <SkeletonAdminBody rows={6} />
      </div>
    );
  }

  if (!currentUser || !canUseReconciliation) return null;

  if (currentUser.role === 'A') {
    return (
      <AdminShell
        user={currentUser}
        title="M-Pesa Recon"
        subtitle="Lookup and recovery"
        maxWidth="max-w-5xl"
      >
        {pageBody}
      </AdminShell>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      <header className="sticky top-0 z-20 border-b border-emerald-100 bg-white/95 shadow-sm backdrop-blur">
        <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-emerald-200 bg-white text-emerald-700 transition hover:bg-emerald-50"
            >
              <Icon name="arrow-left" size="0.875rem" />
            </button>
            <div>
              <h1 className="text-sm font-semibold text-slate-900">M-Pesa Reconciliation</h1>
              <p className="text-[11px] text-emerald-700">Authorized access</p>
            </div>
          </div>
          <div className="relative dropdown-container">
            <button
              type="button"
              onClick={() => setIsMenuOpen((prev) => !prev)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-emerald-200 bg-white text-emerald-700 transition hover:bg-emerald-50"
              aria-label="Open menu"
            >
              <Icon name="list" size="1rem" />
            </button>

            <div className={`absolute right-0 top-full mt-2 w-52 rounded-xl border border-slate-200 bg-white p-2 shadow-lg transition-all ${isMenuOpen ? 'visible opacity-100' : 'invisible opacity-0'}`}>
              <button
                type="button"
                onClick={() => {
                  setIsMenuOpen(false);
                  navigate('/dashboard');
                }}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50"
              >
                <Icon name="house" size="1rem" />
                Dashboard
              </button>
              <button
                type="button"
                onClick={() => setIsMenuOpen(false)}
                className="flex w-full items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-left text-sm font-medium text-emerald-800"
              >
                <Icon name="search" size="1rem" />
                M-Pesa Reconciliation
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsMenuOpen(false);
                  navigate('/support-tickets');
                }}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50"
              >
                <Icon name="life-preserver" size="1rem" />
                Support Tickets
              </button>
              <div className="my-1 h-px bg-slate-100" />
              <button
                type="button"
                onClick={() => {
                  setIsMenuOpen(false);
                  handleLogout();
                }}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-red-600 transition hover:bg-red-50"
              >
                <Icon name="box-arrow-right" size="1rem" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-5 sm:px-6">
        {pageBody}
      </main>
    </div>
  );
}

export default AdminMpesaReconciliationPage;
